import React, {  } from 'react';
import { PropertyFilterOption, PropertyFilterQuery } from "@amzn/awsui-collection-hooks";
import DASHBOARD_CONSTANTS from "../dashboardConstants";
import { DbMainExpression, DbOperator, DbConjunctor, DbExpression } from "src/models/common/TPESearchRequest";
import { Calendar, DateInput, DatePicker, FormField } from '@amzn/awsui-components-react/polaris';

export const OPERATORS_FOR_STRING_PROPERTY = ["="];
export const OPERATORS_EQUALS_ONLY = ["="];
export const OPERATORS_FOR_DATE_PROPERTY = ["="];

export function getDefaultDateFilterOperators() {
    return OPERATORS_FOR_DATE_PROPERTY.map(operator => ({
        operator,
        form: (e:any) => {
          const { value, onChange, filter } = e;
          if (typeof filter === "undefined") {
            return (
              <FormField>
                <DatePicker
                  value={value ?? ""}
                  onChange={event =>
                    onChange(event.detail.value)
                  }
                  placeholder="YYYY/MM/DD"
                  locale="en-US"
                />
              </FormField>
            );
          }
          return (
            <div className="date-form">
              <FormField>
                <DateInput
                  value={value ?? ""}
                  onChange={event =>
                    onChange(event.detail.value)
                  }
                  placeholder="YYYY/MM/DD"
                />
              </FormField>
              <Calendar
                value={value ?? ""}
                onChange={event =>
                  onChange(event.detail.value)
                }
                locale="en-US"
              />
            </div>
          );
        },
        match: "date"
      }));
}

function removeDuplicatesByKeys(arr:any[], keys:string[]) {
    const seen = new Map();
    return arr.filter(item => {
      const key = keys.map(k => item[k]).join('|');
      if (seen.has(key)) {
        return false; // Skip if already seen
      }
      seen.set(key, true);
      return true; // Keep if not seen
    });
  }

/**
 * Takes a list of objects and converts them to a list of PropertyFilterOptions
 * where each object's key is the propertyKey and the value is the value of the object's key.
 * 
 * @param objectsList The list of objects to convert to PropertyFilterOptions
 * @returns A list of PropertyFilterOptions
 */
export function convertObjectsToFilterOptions(objectsList:any[]) : PropertyFilterOption[] {
    return removeDuplicatesByKeys(objectsList.flatMap(obj =>
        Object.entries(obj).map(([key, value]) => {
            return {
                propertyKey: key,
                value: getPropertyValueLabel(key,convertValueToString(value))
            }
        })
    ), ["propertyKey","value"]);
}

/**
 * Takes a value and converts it to a string.
 *
 * @param value The value to convert to a string
 * @returns The string representation of the value
 */
export function convertValueToString(value: unknown): any {
    if (typeof value === 'string' || typeof value === 'number') {
        return value.toString();
    } else if (typeof value === 'boolean') {
        return value.toString();
    } else if (value instanceof Date) {
        return value.toISOString();
    } else if (Array.isArray(value)) {
        return value.map(convertValueToString).join(', ');
    } else if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
    } else {
        return '';
    }
}

/**
 * Takes a key and a value and returns the label or display mode for the value.
 *
 * @param key The key of the value
 * @param value The value to get the label for
 * @returns The label for the value
 */
export function getPropertyValueLabel(key: string, value: string): string {
    switch(key){
        case DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.ACCESSOR:
            return (DASHBOARD_CONSTANTS.ACTION_TYPE_TO_DISPLAY_MAP as any)[value];
        default:
            return value;
    }
}

/**
 * Takes a key and a label and returns the value for the label.
 *
 * @param key The key of the value
 * @param label The label to get the value for
 * @returns The value for the label
 */
export function getPropertyValueFromLabel(key: string, label: string): string {
    if (key === DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.ACCESSOR){
        const value = Object.keys(DASHBOARD_CONSTANTS.ACTION_TYPE_TO_DISPLAY_MAP)
            .find(x => (DASHBOARD_CONSTANTS.ACTION_TYPE_TO_DISPLAY_MAP as any)[x] === label);
        if (value){
            return value;
        }
    }
    return label;
}

/**
 * Takes a PropertyFilterQuery and converts it to a DbMainExpression.
 *
 * @param query The PropertyFilterQuery to convert to a DbMainExpression
 * @returns The DbMainExpression
 */
export function convertPropertyFilterQueryToDbMainExpression(query:PropertyFilterQuery): DbMainExpression {
    if (!query || !query.tokens || query.tokens.length === 0){
        return {} as DbMainExpression;
    }
    return {
        expressions: query.tokens
            .map(x => ({
                operator: convertFilterOperatorToDBOperator(x.operator),
                attributeName: x.propertyKey,
                values: [getPropertyValueFromLabel(x.propertyKey || '', x.value)]
            } as DbExpression)),
        conjunctor: query.operation === "and"? DbConjunctor.AND : DbConjunctor.OR,
    } as DbMainExpression;
}

/**
 * Takes a filter operator and converts it to a DbOperator.
 *
 * @param operator The filter operator to convert to a DbOperator
 * @returns The DbOperator
 */
export function convertFilterOperatorToDBOperator(operator: string): DbOperator {
    switch(operator){
        case "=":
            return DbOperator.EQUALS;
        case "<":
            return DbOperator.LESS_THAN;
        case "<=":
            return DbOperator.LESS_THAN_EQUALS;
        case ">":
            return DbOperator.GREATER_THAN;
        case ">=":
            return DbOperator.GREATER_THAN_EQUALS;
        case ":":
            return DbOperator.CONTAINS;
        case "^":
            return DbOperator.BEGINS_WITH;
        default:
            return DbOperator.EQUALS;
    }
}

/**
 * Takes a filter query and a list of fields and replaces the accessor field with the DB field.
 *
 * @param filterQuery The filter query to replace the accessor field with the DB field
 * @param fields The list of fields to use to find the DB field
 * @returns The filter query with the accessor field replaced with the DB field
 */
export function replaceFilterAccessorFieldWithBDField(filterQuery: PropertyFilterQuery, fields: any): PropertyFilterQuery {
    const fieldsList = Object.values(fields) as any[];
    const filterCopy = JSON.parse(JSON.stringify(filterQuery));
    filterCopy.tokens?.forEach((token: any) => {
        const field = fieldsList.find(x => x.ACCESSOR === token.propertyKey);
        if (field) token.propertyKey = field.DB_FIELD;
    });
    return filterCopy;
}